import { useCallback, useContext } from "react";
import { sessionStore } from "../../../../contexts/SessionContext";
import useAthenaAPIClient from "../../../../hooks/queries/useAthenaAPIClient/useAthenaAPIClient";
import useDashboard from "../../../../hooks/useDashboard";
import { Dashboard } from "../../../../interfaces/Dashboard";
import { PRLockStatus } from "../../../../interfaces/PedagogicalResourcesManagement";
import { Group, Student } from "../../../../interfaces/Session";
import { DuoSession } from "../../../../interfaces/User";
import { userDuoToSessionDuo } from "../../../../utils/session";
import { isPlayable } from "../utils";
import { DuoConstruction } from "./DuoWorkModeItem/AddDuoModal/AddDuoModal";

type TeacherDuoReturn = {
    createDuo: (duo: DuoConstruction, moduleId: string) => Promise<void>;
    numberOfPlayableDuosInModule: (moduleId: string) => number;
    getGroups: (resourceId?: string) => (Group & { locked?: boolean })[];
    getGroupStudents: (
        groupId: string,
        resourceId: string
    ) => (Student & {
        alreadyInDuo: boolean;
        preliminaryTestCompleted: boolean;
    })[];
};

const useTeacherDuo = (): TeacherDuoReturn => {
    const {
        session: { classrooms = [], groups = [] },
        setSession,
    } = useContext(sessionStore);
    const athenaAPIClient = useAthenaAPIClient();
    const { data: dashboard } = useDashboard();

    const createDuo = useCallback<TeacherDuoReturn["createDuo"]>(
        async (duo, moduleId) => {
            const newDuo = await athenaAPIClient.createDuo(
                duo.mentee!.id,
                duo.mentor!.id,
                moduleId
            );
            setSession((session) => ({
                ...session,
                classrooms: updateGroupsWithDuo(
                    session.classrooms,
                    newDuo,
                    duo.group!.id
                ),
                groups: updateGroupsWithDuo(
                    session.groups,
                    newDuo,
                    duo.group!.id
                ),
            }));
        },
        [athenaAPIClient, setSession]
    );

    const getGroups = useCallback<TeacherDuoReturn["getGroups"]>(
        (resourceId) =>
            [...classrooms, ...groups].map((group) => ({
                ...group,
                locked: resourceId
                    ? isResourceLocked(resourceId, group.lockStatus)
                    : undefined,
            })),
        [classrooms, groups]
    );

    const numberOfPlayableDuosInModule = useCallback<
        TeacherDuoReturn["numberOfPlayableDuosInModule"]
    >(
        (moduleId: string) => {
            return getGroups()
                .flatMap((group) => group.duos || [])
                .filter((duo) => duo.moduleId === moduleId && isPlayable(duo))
                .length;
        },
        [getGroups]
    );

    const getGroupStudents = useCallback<TeacherDuoReturn["getGroupStudents"]>(
        (groupId, moduleId) => {
            const allGroups = getGroups();
            const group = allGroups.find(
                (sessionGroup) => sessionGroup.id === groupId
            )!;
            const groupStudents = group.students.map((student) => {
                const studentGroups = getStudentGroups(student.id, allGroups);
                return {
                    ...student,
                    alreadyInDuo:
                        studentGroups.some((group) =>
                            group.duos?.some(
                                ({
                                    mentor,
                                    mentee,
                                    moduleId: duoModuleId,
                                    ...duo
                                }) =>
                                    (mentor.id === student.id ||
                                        mentee.id === student.id) &&
                                    duoModuleId === moduleId &&
                                    isPlayable(duo)
                            )
                        ) ?? false,
                    preliminaryTestCompleted: studentHasCompletedTest(
                        dashboard,
                        moduleId,
                        student.id,
                        studentGroups
                    ),
                };
            });
            return groupStudents;
        },
        [getGroups, dashboard]
    );

    return {
        createDuo,
        numberOfPlayableDuosInModule,
        getGroups,
        getGroupStudents,
    };
};
export default useTeacherDuo;

const isResourceLocked = (moduleId: string, lockStatus: PRLockStatus) => {
    if (lockStatus.moduleIds.includes(moduleId)) return true;
    return false;
};

const updateGroupsWithDuo = (
    groups: Group[] | undefined,
    duo: DuoSession,
    groupId: string
): Group[] | undefined =>
    groups?.map((group) => {
        if (group.id !== groupId) return group;
        return {
            ...group,
            duos: [...(group.duos ?? []), userDuoToSessionDuo(duo)],
        };
    });

const studentHasCompletedTest = (
    dashboard: Dashboard | undefined,
    moduleId: string,
    studentId: string,
    studentGroups: Group[]
) => {
    // Students who have not started a module don't appear in the students list
    // TimeSpent is only filled when exercises are done in the ZPDES
    return (
        Object.keys(
            dashboard?.classrooms
                ?.find((analytics) =>
                    studentGroups
                        .map((group) => group.id)
                        .includes(analytics.id)
                )
                ?.modulesList.find((mod) => mod.id === moduleId)?.students[
                studentId
            ]?.timeSpent || {}
        ).length > 0
    );
};

const getStudentGroups = (studentId: string, groups: Group[]) => {
    return groups.filter((group) =>
        group.students.find((stud) => stud.id === studentId)
    );
};
